import { useState, type CSSProperties, type FC } from 'react';
import type { ClientProject, ClientProjectStage } from '../types';

interface ClientProjectsOverlayProps {
  projects: ClientProject[];
  isOpen: boolean;
  onClose: () => void;
  onUpdateStage: (
    projectId: string,
    stageId: string,
    payload: Partial<ClientProjectStage>,
  ) => Promise<void>;
}

function projectStatusLabel(status: string): string {
  if (status === 'ACTIVE') return 'Активный';
  if (status === 'COMPLETED') return 'Завершён';
  if (status === 'PAUSED') return 'Приостановлен';
  return status;
}

function stageStatusLabel(status: string): string {
  if (status === 'APPROVED') return 'Утверждён';
  if (status === 'IN_PROGRESS') return 'В работе';
  if (status === 'PENDING') return 'Ожидает';
  return status;
}

function stageAccent(status: string): string {
  if (status === 'APPROVED') return '#49ddb7';
  if (status === 'IN_PROGRESS') return '#41d2ff';
  return '#8faebb';
}

/** Client projects — cab-overlay chrome + cabinet-* body. */
export const ClientProjectsOverlay: FC<ClientProjectsOverlayProps> = ({
  projects,
  isOpen,
  onClose,
  onUpdateStage,
}) => {
  const [selectedId, setSelectedId] = useState(projects[0]?.id || '');
  const [commentInput, setCommentInput] = useState('');
  const [activeStageId, setActiveStageId] = useState<string | null>(null);

  if (!isOpen) return null;

  const selected = projects.find((p) => p.id === selectedId) || projects[0];

  const handleStageApprove = async (stage: ClientProjectStage) => {
    if (!selected) return;
    await onUpdateStage(selected.id, stage.id, {
      status: 'APPROVED',
      progress_percent: 100,
      comment: commentInput || 'Этап утвержден клиентом',
    });
    setCommentInput('');
    setActiveStageId(null);
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true" aria-labelledby="projects-title">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Проекты клиента</small>
              <h2 id="projects-title">Мои проекты</h2>
              <p>Ведение публикаций, диссертационных работ и грантовых соглашений</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div
          className="cab-body"
          style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 0.8fr) minmax(0, 1.2fr)', gap: 14 }}
        >
          <aside className="cabinet-stack">
            {projects.length === 0 ? (
              <div className="cabinet-note">Нет активных проектов</div>
            ) : (
              projects.map((proj) => {
                const isSel = selected?.id === proj.id;
                return (
                  <button
                    key={proj.id}
                    type="button"
                    className="cabinet-card"
                    style={
                      {
                        '--accent': isSel ? '#49ddb7' : '#41d2ff',
                        textAlign: 'left',
                        cursor: 'pointer',
                        width: '100%',
                        color: 'inherit',
                        font: 'inherit',
                      } as CSSProperties
                    }
                    onClick={() => setSelectedId(proj.id)}
                  >
                    <div className="cabinet-actions" style={{ marginBottom: 6 }}>
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': '#49ddb7' } as CSSProperties}
                      >
                        {proj.project_code}
                      </span>
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': '#8faebb' } as CSSProperties}
                      >
                        {projectStatusLabel(proj.status)}
                      </span>
                    </div>
                    <strong style={{ display: 'block' }}>{proj.title}</strong>
                    <div className="cabinet-actions" style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 11, color: '#8faebb' }}>
                        Готовность: {proj.progress_percent}%
                      </span>
                      <span className="cabinet-spacer" />
                      <span style={{ fontSize: 11, color: '#8faebb' }}>
                        Срок: {proj.end_date || '—'}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </aside>

          <main className="cabinet-stack">
            {selected ? (
              <>
                <section className="cabinet-hero" style={{ '--accent': '#49ddb7' } as CSSProperties}>
                  <div>
                    <div className="cabinet-eyebrow">
                      {selected.project_code} · {selected.service_type}
                    </div>
                    <h2>{selected.title}</h2>
                    <p>
                      {selected.description ||
                        `Период: ${selected.start_date} — ${selected.end_date}`}
                    </p>
                  </div>
                </section>

                <section className="cabinet-kpis">
                  <article className="cabinet-kpi" style={{ '--accent': '#41d2ff' } as CSSProperties}>
                    <small>Координатор</small>
                    <strong style={{ fontSize: 16 }}>
                      {selected.manager_name || 'Ибраев Арман'}
                    </strong>
                    <span>{selected.manager_contact || '—'}</span>
                  </article>
                  <article className="cabinet-kpi" style={{ '--accent': '#49ddb7' } as CSSProperties}>
                    <small>Бюджет</small>
                    <strong style={{ fontSize: 18 }}>{selected.budget || '—'}</strong>
                    <span>Оплата по этапам</span>
                  </article>
                  <article className="cabinet-kpi" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
                    <small>Прогресс</small>
                    <strong>{selected.progress_percent}%</strong>
                    <span>общий прогресс</span>
                  </article>
                  <article className="cabinet-kpi" style={{ '--accent': '#8faebb' } as CSSProperties}>
                    <small>Этапы</small>
                    <strong>{selected.stages?.length || 0}</strong>
                    <span>в проекте</span>
                  </article>
                </section>

                <section>
                  <div className="cabinet-actions" style={{ marginBottom: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 15 }}>Этапы реализации проекта</h3>
                    <span className="cabinet-spacer" />
                    <span style={{ fontSize: 12, color: '#8faebb' }}>
                      Всего этапов: {selected.stages?.length || 0}
                    </span>
                  </div>
                  <div className="cabinet-stack">
                    {selected.stages?.map((stage) => {
                      const isApproved = stage.status === 'APPROVED';
                      const isInProgress = stage.status === 'IN_PROGRESS';
                      return (
                        <article
                          key={stage.id}
                          className="cabinet-card"
                          style={{ '--accent': stageAccent(stage.status) } as CSSProperties}
                        >
                          <div className="cabinet-actions">
                            <span
                              className="cabinet-badge"
                              style={{ '--badge': stageAccent(stage.status) } as CSSProperties}
                            >
                              {stage.stage_number}
                            </span>
                            <strong>{stage.title}</strong>
                            <span className="cabinet-spacer" />
                            <span
                              className="cabinet-badge"
                              style={{ '--badge': stageAccent(stage.status) } as CSSProperties}
                            >
                              {stageStatusLabel(stage.status)}
                            </span>
                          </div>
                          <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12 }}>
                            Дедлайн этапа: {stage.deadline || 'По графику'}
                          </p>
                          {stage.description ? (
                            <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12 }}>
                              {stage.description}
                            </p>
                          ) : null}

                          {stage.result_file_path ? (
                            <div className="cabinet-note ok" style={{ marginTop: 10 }}>
                              <div className="cabinet-actions">
                                <span>
                                  {stage.result_file_name || 'артефакт_этапа.pdf'}
                                </span>
                                <span className="cabinet-spacer" />
                                <a
                                  href={stage.result_file_path}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="cabinet-btn primary"
                                >
                                  Скачать файл этапа
                                </a>
                              </div>
                            </div>
                          ) : null}

                          {stage.comment ? (
                            <div className="cabinet-note" style={{ marginTop: 10 }}>
                              Комментарий: {stage.comment}
                            </div>
                          ) : null}

                          {isInProgress ? (
                            <div className="cabinet-actions" style={{ marginTop: 10 }}>
                              {activeStageId === stage.id ? (
                                <>
                                  <input
                                    className="cabinet-input"
                                    type="text"
                                    value={commentInput}
                                    onChange={(e) => setCommentInput(e.target.value)}
                                    placeholder="Замечания или подтверждение этапа..."
                                    style={{ flex: 1 }}
                                  />
                                  <button
                                    type="button"
                                    className="cabinet-btn primary"
                                    onClick={() => void handleStageApprove(stage)}
                                  >
                                    Утвердить
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="cabinet-btn"
                                  onClick={() => setActiveStageId(stage.id)}
                                >
                                  Подтвердить приемку этапа
                                </button>
                              )}
                            </div>
                          ) : null}

                          {isApproved ? (
                            <div className="cabinet-note ok" style={{ marginTop: 10 }}>
                              Этап утверждён клиентом
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                </section>
              </>
            ) : (
              <div className="cabinet-note">Выберите проект для просмотра этапов</div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
